import type { ConsoleRole } from '@fitting-room/contracts'

/**
 * What the console's navigation is made of, and who sees which part of it.
 *
 * Twelve tabs in a row was too many to tell apart, and half of them were
 * opened once a week. So the tabs are now SECTIONS — a handful a person can
 * hold in their head — and each section is a group of the pages that were
 * already there, at the addresses they already had. A bookmark to
 * `/admin/alerts` still lands on Alerts; it is just that the tab lit above it
 * now reads "To do". Nothing here owns a route: `App.tsx` does, and a section
 * only says which of those routes belong together.
 *
 * Plain data and plain functions, apart from `AdminLayout`, because which
 * tabs a stylist sees and which one is lit are the two things that can be
 * wrong without looking wrong, and neither needs a browser to check.
 */

/*
 * The labels are American, the paths are not.
 *
 * `catalogue` and `enquiries` are the words the contract, the API routes and
 * the Postgres columns are spelled with, and renaming those is a migration
 * rather than a copy change. What a stylist in Los Angeles reads is "Catalog"
 * and "Inquiries", which is the half of the split that matters.
 */

export interface ConsolePage {
  readonly to: string
  readonly label: string
  /** `true`: this address and nothing under it. `/admin` would otherwise be lit on every screen. */
  readonly end: boolean
  readonly owner: boolean
}

export interface ConsoleSection {
  readonly id: string
  readonly label: string
  /**
   * `tab` is a tab on the header's rule. `gear` is the settings button beside
   * Sign out: the shop's policy is set once and revisited rarely, so it is
   * one click away from every screen without spending a tab on it.
   */
  readonly placement: 'tab' | 'gear'
  /** In the order the sub-nav draws them. The first one the role can see is where the tab goes. */
  readonly pages: readonly ConsolePage[]
  /**
   * Addresses that belong to the section without being a page in its nav,
   * so the section stays lit while they are open: the pull sheet, which is
   * one appointment's page (reached by its address now that the drawer no
   * longer links to it), and Sync gowns, which is an action
   * taken from the Catalog page rather than a page beside it. Filtered by role
   * like the pages, so a stylist refused at `/admin/sync` sees nothing lit.
   */
  readonly alsoAt?: readonly ConsoleAddress[]
}

export interface ConsoleAddress {
  readonly to: string
  readonly owner: boolean
}

/*
 * `owner: true` is the whole of the console's role rule, and most pages not
 * carrying it is the point: appointments, what is waiting to be done, orders
 * and the catalog ARE a stylist's day. See `OWNER_ONLY` in
 * `packages/contracts`.
 *
 * Hidden rather than shown-and-disabled. A tab a stylist can see, click and be
 * refused teaches them that the console is broken; a tab that is not there is
 * a console that is simply smaller for them. The endpoints behind both agree
 * either way — `console-identity.test.ts` proves that, and a hidden tab is a
 * courtesy rather than a permission.
 */
export const SECTIONS: readonly ConsoleSection[] = [
  {
    id: 'appointments',
    label: 'Appointments',
    placement: 'tab',
    pages: [{ to: '/admin', label: 'Appointments', end: true, owner: false }],
    alsoAt: [{ to: '/admin/visits', owner: false }],
  },
  /*
   * Everything somebody has to act on, whichever list it sits in: the
   * customers someone means to get back to, what is going wrong with an order,
   * and the inquiries nobody has claimed. Three pages under one tab rather than
   * one merged feed — each list has its own filters and its own way of being
   * cleared, and the tab is where the alert count rides (see `AdminLayout`).
   * Follow-ups first: they are what the appointments end in.
   */
  {
    id: 'to-do',
    label: 'To do',
    placement: 'tab',
    pages: [
      { to: '/admin/follow-ups', label: 'Follow-ups', end: false, owner: false },
      { to: '/admin/alerts', label: 'Alerts', end: false, owner: false },
      { to: '/admin/enquiries', label: 'Inquiries', end: false, owner: false },
    ],
  },
  /* What the appointment ended in, when the customer bought. A stylist's, like the appointment. */
  {
    id: 'orders',
    label: 'Orders',
    placement: 'tab',
    pages: [{ to: '/admin/orders', label: 'Orders', end: false, owner: false }],
  },
  /*
   * Airtable, then the catalog, then the website: the order the work happens
   * in. Airtable lands in the catalog, a person agrees to it, and only then
   * does any of it go on the website. Sync gowns is how Airtable lands, and it
   * is an action taken on the catalog rather than a page beside it — so it is
   * the owner's button on the Catalog page, its address kept here only so the
   * tab stays lit while it runs. Catalog, then Publish, is the rest of that
   * order; a sub-nav that put Publish first would suggest the website could be
   * ahead of the catalog, which is the one thing this whole feature exists to
   * prevent. A stylist, who has neither Sync nor Publish, sees Catalog on its
   * own and no sub-nav at all.
   */
  {
    id: 'catalog',
    label: 'Catalog',
    placement: 'tab',
    pages: [
      { to: '/admin/catalogue', label: 'Catalog', end: false, owner: false },
      { to: '/admin/storefront', label: 'Publish', end: false, owner: true },
    ],
    alsoAt: [{ to: '/admin/sync', owner: true }],
  },
  /*
   * The money looked at whole — the takings and every void, then what each
   * person is owed on the orders they sold — read by the owner, not worked
   * through by a stylist. "Reports" rather than "Money": it is what the owner
   * opens them to do.
   */
  {
    id: 'reports',
    label: 'Reports',
    placement: 'tab',
    pages: [
      { to: '/admin/payments', label: 'Payments', end: false, owner: true },
      { to: '/admin/commission', label: 'Commission', end: false, owner: true },
    ],
  },
  /*
   * The team, the back office's lists and numbers, the size chart, and the
   * website's theme: the shop's policy, not a stylist's day. The theme is here
   * rather than beside Publish because it is set once and changed rarely; it
   * is not a step anything in the catalog waits on.
   */
  {
    id: 'settings',
    label: 'Settings',
    placement: 'gear',
    pages: [
      { to: '/admin/settings', label: 'General', end: false, owner: true },
      { to: '/admin/theme', label: 'Theme', end: false, owner: true },
    ],
  },
]

/**
 * The sections this role sees, each holding only the pages it may open, and
 * none left holding nothing.
 *
 * `role === 'owner'` rather than the contract's `atLeast`, here and in
 * `OwnerOnly`, for the same reason `SyncPage` and `StorefrontPage` spell it
 * that way: `atLeast` lives in the contracts barrel, and a VALUE import from
 * that barrel pulls every zod schema into the bundle a bride loads on her
 * phone. `contracts-boundary.test.ts` holds the line. Owner is the top rung,
 * so the comparison and the ladder cannot disagree.
 */
export function sectionsFor(role: ConsoleRole | null, sections: readonly ConsoleSection[] = SECTIONS): ConsoleSection[] {
  // A marketer has no section here: their console is one read-only list,
  // drawn by `MarketerConsole` in `AdminLayout`, and none of these pages.
  if (role === 'marketer') return []
  const out: ConsoleSection[] = []
  for (const section of sections) {
    const mayOpen = (address: ConsoleAddress) => !address.owner || role === 'owner'
    const pages = section.pages.filter(mayOpen)
    if (pages.length > 0) out.push({ ...section, pages, alsoAt: (section.alsoAt ?? []).filter(mayOpen) })
  }
  return out
}

/** `/admin/` and `/admin` are the same screen. */
function normalize(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

/** Whether `pathname` is this address — or, unless `end`, anything under it — the way `NavLink` decides. */
export function isAt(pathname: string, to: string, end: boolean): boolean {
  const path = normalize(pathname)
  return path === to || (!end && path.startsWith(`${to}/`))
}

/**
 * The section the current screen is in, among those the role sees.
 *
 * Only the addresses the role can open count — `sectionsFor` has already
 * dropped the rest, `alsoAt` included: a stylist who reached `/admin/sync`
 * by a pasted link is looking at `OwnerOnly`'s refusal, and lighting Catalog
 * above it would say the refusal is part of their catalog.
 */
export function sectionAt(sections: readonly ConsoleSection[], pathname: string): ConsoleSection | undefined {
  return sections.find(
    (section) =>
      section.pages.some((page) => isAt(pathname, page.to, page.end)) ||
      (section.alsoAt ?? []).some((address) => isAt(pathname, address.to, false)),
  )
}

/**
 * Where a section's tab goes: its first page, of those the role can see. Call
 * with `sectionsFor`'s output, which never holds an empty section; the
 * fallback is for the type, not for a case that happens.
 */
export function landingOf(section: ConsoleSection): string {
  return section.pages[0]?.to ?? '/admin'
}

/** A section with more than one page the role can see draws a sub-nav under the tabs; one with a single page does not. */
export function hasSubNav(section: ConsoleSection): boolean {
  return section.pages.length > 1
}

/**
 * What a section's tab announces as `aria-current`.
 *
 * `page` when the tab IS the page — a section of one, as every tab used to
 * be. `true` when the section holds several and the sub-nav underneath names
 * the page: two links both claiming to be the current page would leave a
 * screen reader user unsure which one they are on.
 */
export function tabCurrent(section: ConsoleSection, current: ConsoleSection | undefined): 'page' | 'true' | undefined {
  if (current?.id !== section.id) return undefined
  return hasSubNav(section) ? 'true' : 'page'
}
